import { useEffect, useRef, useState } from "react";
import { Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Mascot, MascotMood } from "@/components/assistant/mascot";
import { CaptchaRequiredError, solveCaptcha, verifyChat } from "@/lib/verify-client";
import "@/components/assistant/assistant.css";

interface Line { role: "user" | "assistant"; text: string }

const TEXTS = {
  pt: {
    title: "Dúvidas sobre este resultado?",
    hint: "O chat explica a verificação. Ele não vê outros documentos nem dados completos.",
    placeholder: "Pergunte sobre a verificação…",
    send: "Enviar",
    chips: ["O que é hash?", "Vale na Justiça?", "Quem assinou?", "Deu alterado, e agora?"],
    offline: "Não consegui responder agora. Tente de novo em instantes.",
    captcha: "Confirme que você não é um robô para continuar.",
  },
  en: {
    title: "Questions about this result?",
    hint: "The chat explains the verification. It sees no other documents and no full personal data.",
    placeholder: "Ask about the verification…",
    send: "Send",
    chips: ["What is a hash?", "Is it legally valid?", "Who signed?", "It says modified, now what?"],
    offline: "Couldn't answer right now. Try again in a moment.",
    captcha: "Confirm you are human to continue.",
  },
};

/** Chat panel of the public verification page: one verification result, nothing else. */
export function VerifyChat({ token, language, resultKey }: { token: string; language: string; resultKey: string }) {
  const t = language === "en" ? TEXTS.en : TEXTS.pt;
  const [lines, setLines] = useState<Line[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [mood, setMood] = useState<MascotMood>("wave");
  const [conversationId, setConversationId] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const captchaRef = useRef<string | undefined>(undefined);

  // A new verification = a new conversation.
  useEffect(() => { setLines([]); setConversationId(null); setMood("wave"); }, [resultKey]);
  useEffect(() => { listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" }); }, [lines, busy]);

  const send = async (text: string) => {
    const message = text.trim();
    if (!message || busy) return;
    setInput("");
    setLines((l) => [...l, { role: "user", text: message }]);
    setBusy(true);
    setMood("thinking");
    try {
      let reply;
      try {
        reply = await verifyChat({ token, conversationId, message, lang: language }, captchaRef.current);
      } catch (err) {
        if (err instanceof CaptchaRequiredError && err.siteKey) {
          captchaRef.current = await solveCaptcha(err.siteKey);
          reply = await verifyChat({ token, conversationId, message, lang: language }, captchaRef.current);
        } else throw err;
      }
      setConversationId(reply.conversationId);
      setLines((l) => [...l, { role: "assistant", text: reply.reply.text }]);
      setMood((reply.reply.mood as MascotMood) || "happy");
    } catch {
      setLines((l) => [...l, { role: "assistant", text: t.offline }]);
      setMood("sorry");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border bg-card shadow-sm" data-testid="verify-chat">
      <div className="flex items-center gap-3 border-b px-4 py-3">
        <Mascot mood={mood} size={44} />
        <div className="min-w-0">
          <p className="font-medium leading-tight">{t.title}</p>
          <p className="text-xs text-muted-foreground">{t.hint}</p>
        </div>
      </div>
      <div ref={listRef} className="max-h-72 space-y-2 overflow-y-auto px-4 py-3" data-testid="verify-chat-lines">
        {lines.length === 0 && (
          <div className="flex flex-wrap gap-2" data-testid="verify-chat-chips">
            {t.chips.map((c) => (
              <button key={c} type="button" onClick={() => void send(c)} className="rounded-full border bg-background px-3 py-1 text-sm hover:bg-muted">
                {c}
              </button>
            ))}
          </div>
        )}
        {lines.map((l, i) => (
          <div key={i} className={`flex ${l.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`verify-chat-line-${l.role}`}>
            <div className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3 py-2 text-sm ${l.role === "user" ? "rounded-br-sm bg-primary text-primary-foreground" : "rounded-bl-sm bg-muted"}`}>
              {l.text}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start" data-testid="verify-chat-typing">
            <div className="rounded-2xl rounded-bl-sm bg-muted px-3 py-2 text-sm text-muted-foreground">…</div>
          </div>
        )}
      </div>
      <form
        className="flex items-center gap-2 border-t p-3"
        onSubmit={(e) => { e.preventDefault(); void send(input); }}
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value.slice(0, 500))}
          placeholder={t.placeholder}
          className="h-10 flex-1 rounded-xl border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-primary/40"
          data-testid="verify-chat-input"
          maxLength={500}
        />
        <Button type="submit" size="icon" className="h-10 w-10 shrink-0 rounded-xl" disabled={busy || !input.trim()} aria-label={t.send} data-testid="verify-chat-send">
          <Send className="h-4 w-4" />
        </Button>
      </form>
    </div>
  );
}
